/**
 * SubscribeToBeVisiblePopup
 *
 * One-time modal shown the first time an unsubscribed coach lands on
 * their dashboard after finishing their profile. Complements
 * SubscribeToBeVisibleBanner (the persistent, non-dismissable strip) with
 * a single up-front nudge — unlike the banner, this popup is dismissable
 * and never reappears once the coach has seen and closed it.
 *
 * Render conditions (all must be true):
 *   - currentUser is loaded and is a provider (coach)
 *   - subscriptionDetails.subscription tier is 0 (no active paid plan)
 *   - not currently on /account/payments/* (avoid duplicate CTA there)
 *   - localStorage flag for this popup hasn't been set yet
 *
 * The flag is written when the popup is dismissed (X, backdrop click,
 * Escape, or the CTA) — mirrors SubscriptionMigrationBanner's pattern —
 * so a page refresh before the coach interacts with it will show the
 * popup again, but once acknowledged it's gone for good.
 */

import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { Link, withRouter } from 'react-router-dom';
import { X } from 'react-feather';
import { isInstallmentPaymentLocked } from '../../../util/installmentLock';

const STORAGE_KEY = 'viveka:subscribePopupShown';

const SubscribeToBeVisiblePopup = ({ currentUser, location, onVisibilityChange }) => {
  const [isOpen, setIsOpen] = useState(false);

  // Lets the parent (MainDashboardWrapper) hold off on the coach onboarding
  // tooltip tour while this modal is up — otherwise both can render at once,
  // stacking a tooltip on top of the paywall.
  useEffect(() => {
    if (onVisibilityChange) onVisibilityChange(isOpen);
  }, [isOpen, onVisibilityChange]);

  const isProvider = currentUser?.attributes?.profile?.publicData?.isProvider === true;
  const subscriptionDetails = currentUser?.attributes?.profile?.metadata?.subscriptionDetails;
  const tier =
    typeof subscriptionDetails?.subscription === 'number' ? subscriptionDetails.subscription : 0;
  const onPaymentsPage = !!location && location.pathname.indexOf('/account/payments') === 0;

  // A Lifetime-installments coach whose payment failed also has tier 0 (see
  // installmentLifetime.js's lockForFailedInstallment), but they already
  // have a subscription — InstallmentPaymentFailedBanner covers them with
  // specific "fix your payment" copy, not this generic "start a free
  // trial" popup.
  const isEligible =
    !!currentUser && isProvider && tier < 1 && !onPaymentsPage && !isInstallmentPaymentLocked(currentUser);

  useEffect(() => {
    if (!isEligible) return;
    try {
      if (!window.localStorage.getItem(STORAGE_KEY)) setIsOpen(true);
    } catch (e) {
      setIsOpen(true);
    }
  }, [isEligible]);

  useEffect(() => {
    if (!isOpen) return;
    const handleKeydown = e => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', handleKeydown);
    return () => window.removeEventListener('keydown', handleKeydown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = previous;
    };
  }, [isOpen]);

  const handleClose = () => {
    setIsOpen(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, 'true');
    } catch (e) {
      /* localStorage unavailable; popup will re-open on next mount */
    }
  };

  if (!isOpen) return null;

  return (
    <div
      onClick={handleClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="subscribe-popup-title"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 9999,
        backgroundColor: 'rgba(15, 23, 42, 0.65)',
        backdropFilter: 'blur(4px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '460px',
          padding: '32px 30px',
          borderRadius: '20px',
          background: 'linear-gradient(135deg, #064E3B 0%, #0F3F4A 55%, #1E1040 100%)',
          border: '1px solid rgba(251, 191, 36, 0.35)',
          boxShadow: '0 30px 80px rgba(15, 23, 42, 0.55)',
        }}
      >
        <button
          type="button"
          onClick={handleClose}
          aria-label="Close"
          style={{
            position: 'absolute',
            top: '14px',
            right: '14px',
            width: '34px',
            height: '34px',
            borderRadius: '50%',
            border: '1px solid rgba(255,255,255,0.18)',
            background: 'rgba(255,255,255,0.08)',
            color: '#FFFFFF',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: 0,
          }}
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        <div style={{ textAlign: 'center' }}>
          <span
            aria-hidden="true"
            style={{
              display: 'inline-block',
              width: '10px',
              height: '10px',
              borderRadius: '50%',
              background: '#FBBF24',
              boxShadow: '0 0 0 6px rgba(251, 191, 36, 0.18)',
              marginBottom: '18px',
            }}
          />

          <h2
            id="subscribe-popup-title"
            style={{
              margin: 0,
              fontSize: '22px',
              fontWeight: 800,
              color: '#FFFFFF',
              letterSpacing: '-0.02em',
              lineHeight: 1.25,
            }}
          >
            Ready to make your profile visible?
          </h2>

          <p
            style={{
              margin: '14px auto 0',
              color: '#CBD5E1',
              fontSize: '14px',
              lineHeight: 1.6,
              maxWidth: '360px',
            }}
          >
            Choose a membership to publish your profile and start connecting with the Viveka
            community. Try any monthly plan free for 7 days.
          </p>

          <div style={{ marginTop: '26px' }}>
            <Link
              to="/account/payments/pricing"
              onClick={handleClose}
              style={{
                background: '#FBBF24',
                color: '#0F172A',
                fontWeight: 700,
                padding: '13px 30px',
                borderRadius: '10px',
                fontSize: '14px',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center',
                gap: '8px',
              }}
            >
              Start my free trial
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  currentUser: state.user.currentUser,
});

export default withRouter(connect(mapStateToProps)(SubscribeToBeVisiblePopup));
